/**
 * Timezone-correct operating-hours evaluation.
 *
 * Pure + reusable (API checkout validation, client status display). Unlike the
 * legacy client helper — which assumed the viewer's local timezone — this takes
 * an explicit IANA `timeZone` so it is correct server-side (where the process
 * runs in UTC) as well as on any client.
 *
 * Semantics match `Restaurant.isOpen`: a day's window is `[open, close]`
 * inclusive, `by_appointment` is always open, and a `closed` day (or a closed
 * special-hours entry) is never open. Special hours for a given calendar date
 * override the regular weekly schedule.
 */

import type { DayHours, LocationHoursMode, SpecialHours } from '../locations/Location';

/** Minimal shape needed to evaluate operating hours (a subset of IRestaurant). */
export interface OperatingHoursInput {
  readonly hoursMode?: LocationHoursMode;
  readonly operatingHours?: Record<string, DayHours>;
  readonly specialHours?: readonly SpecialHours[];
}

/**
 * The day keys used by `operatingHours` — FULL lowercase weekday names
 * (`'monday'`…`'sunday'`), matching how the data is stored (the model's
 * `getDefaultOperatingHours`, the management ordering-hours editor, and the
 * client status display all key by full name). Using 3-letter keys here was a
 * bug: the lookup always missed → every time read as "closed" → checkout
 * rejected every order with RESTAURANT_CLOSED / SCHEDULED_TIME_UNAVAILABLE.
 */
const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

/**
 * Aliases for non-canonical day keys seen in legacy/seed/integration data.
 * Used only by [canonicalDayKey] to heal input at the boundary — the rest of
 * the codebase keys exclusively by the canonical full lowercase name.
 */
const DAY_KEY_ALIASES: Readonly<Record<string, string>> = {
  mon: 'monday',
  tue: 'tuesday',
  tues: 'tuesday',
  wed: 'wednesday',
  weds: 'wednesday',
  thu: 'thursday',
  thur: 'thursday',
  thurs: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

/**
 * Canonicalize a single day key to the full lowercase weekday name
 * (`'mon'`/`'MONDAY'`/`' Monday '` → `'monday'`). Unknown values pass through
 * lowercased+trimmed so callers can still detect a miss.
 */
export function canonicalDayKey(day: string): string {
  const k = day.trim().toLowerCase();
  if ((DAY_KEYS as readonly string[]).includes(k)) return k;
  return DAY_KEY_ALIASES[k] ?? k;
}

/**
 * Anti-corruption layer: normalize every key of an `operatingHours` map to the
 * canonical full lowercase weekday name. Run ONCE at the deserialization
 * boundary (the `Restaurant` constructor / `fromJSON`) so every downstream
 * consumer can look up by canonical key with no per-call fallback. Idempotent;
 * returns the input unchanged when it is `undefined`.
 */
export function normalizeOperatingHoursKeys<T>(
  raw: Record<string, T> | undefined,
): Record<string, T> | undefined {
  if (!raw) return raw;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(raw)) {
    out[canonicalDayKey(key)] = value;
  }
  return out;
}

/**
 * Project a UTC instant into a target timezone's wall-clock parts.
 * Returns the weekday key (`'monday'`), `HH:mm`, and `YYYY-MM-DD`.
 */
function localParts(
  instant: Date,
  timeZone: string,
): { dayKey: string; hhmm: string; ymd: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const dayKey = get('weekday').toLowerCase();
  // `hour12: false` can emit '24' at midnight in some ICU builds — normalize.
  let hour = get('hour');
  if (hour === '24') hour = '00';
  const hhmm = `${hour.padStart(2, '0')}:${get('minute').padStart(2, '0')}`;
  const ymd = `${get('year')}-${get('month')}-${get('day')}`;
  return { dayKey, hhmm, ymd };
}

/**
 * Is the restaurant open at `instant`, evaluated in `timeZone`?
 *
 * Returns `true` for `by_appointment` restaurants and when `timeZone` is
 * invalid (fail-open — callers gate enforcement on a confidently-resolved
 * timezone, so we never block a checkout because of a bad tz string).
 */
export function isLocationOpenAt(
  input: OperatingHoursInput,
  instant: Date,
  timeZone: string,
): boolean {
  if (input.hoursMode === 'by_appointment') return true;

  let dayKey: string;
  let hhmm: string;
  let ymd: string;
  try {
    ({ dayKey, hhmm, ymd } = localParts(instant, timeZone));
  } catch {
    return true; // Unknown/invalid timezone — do not block.
  }

  // Special hours for this calendar date override the weekly schedule.
  const special = input.specialHours?.find((s) => s.date === ymd);
  if (special) {
    if (special.isClosed) return false;
    if (special.openTime && special.closeTime) {
      return hhmm >= special.openTime && hhmm <= special.closeTime;
    }
    return true; // Open all day (special date present, no explicit window).
  }

  const day = input.operatingHours?.[dayKey];
  if (!day || day.closed) return false;
  return hhmm >= day.open && hhmm <= day.close;
}

/**
 * The weekly schedule online ordering and the marketplaces follow.
 *
 * A location keeps TWO weekly schedules. `operatingHours` is its BUSINESS hours:
 * the website, Google, the AI assistant, reports, shift tasks and the windows the
 * tills and gateway update in. `onlineOrderingHours` is when online and
 * marketplace orders are taken. A location that has not set ordering hours takes
 * orders during its business hours, so absent, `null` or empty all mean "same as
 * business hours". Special hours (holidays) are ONE list, kept with the business
 * hours, and apply to both.
 *
 * Every reader of ordering hours goes through this — a reader that reads
 * `operatingHours` directly quietly ignores ordering hours the owner set.
 */
export function onlineOrderingHoursOf(input: {
  readonly operatingHours?: Record<string, DayHours>;
  readonly onlineOrderingHours?: Record<string, DayHours> | null;
}): Record<string, DayHours> | undefined {
  const own = input.onlineOrderingHours;
  return normalizeOperatingHoursKeys(
    own && Object.keys(own).length > 0 ? own : input.operatingHours,
  );
}

/** Exposed for callers that need the canonical day-key ordering. */
export { DAY_KEYS };
